/**
 * Create Group Modal
 * Modal dialog for creating new distribution groups
 */
import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  AtSymbolIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useCreateDistributionGroup } from '../../hooks/useDistributionGroups';
import { useDomains } from '../../hooks/useMailboxes';
import type { GroupType, MemberJoinRestriction, MemberDepartRestriction } from '../../types/admin.types';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  localPart: string;
  domainId: string;
  displayName: string;
  description: string;
  groupType: GroupType;
  memberJoinRestriction: MemberJoinRestriction;
  memberDepartRestriction: MemberDepartRestriction;
}

const initialFormData: FormData = {
  localPart: '',
  domainId: '',
  displayName: '',
  description: '',
  groupType: 'distribution',
  memberJoinRestriction: 'closed',
  memberDepartRestriction: 'closed',
};

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch domains
  const { data: domainsData, isLoading: isLoadingDomains } = useDomains();
  const createGroupMutation = useCreateDistributionGroup();

  // Set default domain when domains load
  useEffect(() => {
    if (domainsData?.length && !formData.domainId) {
      const primaryDomain = domainsData.find((d) => d.is_primary);
      setFormData((prev) => ({
        ...prev,
        domainId: primaryDomain?.domain_id || domainsData[0].domain_id,
      }));
    }
  }, [domainsData, formData.domainId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setErrors({});
      setShowAdvanced(false);
    }
  }, [isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.localPart.trim()) {
      newErrors.localPart = 'Email address is required';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.localPart)) {
      newErrors.localPart = 'Invalid characters in email address';
    }

    if (!formData.domainId) {
      newErrors.domainId = 'Please select a domain';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await createGroupMutation.mutateAsync({
        domainId: formData.domainId,
        localPart: formData.localPart,
        displayName: formData.displayName,
        description: formData.description || undefined,
        groupType: formData.groupType,
        memberJoinRestriction: formData.memberJoinRestriction,
        memberDepartRestriction: formData.memberDepartRestriction,
      });
      onSuccess();
    } catch (err: any) {
      if (err.response?.data?.error) {
        setErrors({ submit: err.response.data.error });
      } else {
        setErrors({ submit: 'Failed to create group. Please try again.' });
      }
    }
  };

  // Get selected domain name for preview
  const selectedDomain = domainsData?.find((d) => d.domain_id === formData.domainId);
  const emailPreview = formData.localPart && selectedDomain
    ? `${formData.localPart.toLowerCase()}@${selectedDomain.domain}`
    : '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-lex-accent-soft rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-lex-accent" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Create Distribution Group
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Create a new email distribution list
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Error message */}
              {errors.submit && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-700 dark:text-red-300">{errors.submit}</p>
                </div>
              )}

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="e.g., Marketing Team"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent ${
                    errors.displayName
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-500">{errors.displayName}</p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <div className="relative flex-1">
                    <AtSymbolIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.localPart}
                      onChange={(e) => setFormData({ ...formData, localPart: e.target.value })}
                      placeholder="marketing"
                      className={`w-full pl-10 pr-3 py-2 border rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent ${
                        errors.localPart
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  <span className="inline-flex items-center px-3 border-y border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    @
                  </span>
                  <select
                    value={formData.domainId}
                    onChange={(e) => setFormData({ ...formData, domainId: e.target.value })}
                    disabled={isLoadingDomains}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
                  >
                    {isLoadingDomains ? (
                      <option>Loading...</option>
                    ) : (
                      domainsData?.map((domain) => (
                        <option key={domain.domain_id} value={domain.domain_id}>
                          {domain.domain}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {errors.localPart && (
                  <p className="mt-1 text-sm text-red-500">{errors.localPart}</p>
                )}
                {emailPreview && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Group email: <span className="font-medium">{emailPreview}</span>
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for this group"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
                />
              </div>

              {/* Group Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Type
                </label>
                <select
                  value={formData.groupType}
                  onChange={(e) => setFormData({ ...formData, groupType: e.target.value as GroupType })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
                >
                  <option value="distribution">Distribution List</option>
                  <option value="security">Security Group</option>
                  <option value="mail_enabled_security">Mail-Enabled Security Group</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Distribution lists are used for sending emails to multiple recipients.
                </p>
              </div>

              {/* Advanced Settings Toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center text-sm text-lex-accent hover:underline"
              >
                <InformationCircleIcon className="w-4 h-4 mr-1" />
                {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
              </button>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Member Join Policy
                    </label>
                    <select
                      value={formData.memberJoinRestriction}
                      onChange={(e) =>
                        setFormData({ ...formData, memberJoinRestriction: e.target.value as MemberJoinRestriction })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
                    >
                      <option value="closed">Closed (Owner approval required)</option>
                      <option value="open">Open (Anyone can join)</option>
                      <option value="approval_required">Approval Required</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Member Leave Policy
                    </label>
                    <select
                      value={formData.memberDepartRestriction}
                      onChange={(e) =>
                        setFormData({ ...formData, memberDepartRestriction: e.target.value as MemberDepartRestriction })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
                    >
                      <option value="closed">Closed (Cannot leave on their own)</option>
                      <option value="open">Open (Members can leave freely)</option>
                      <option value="approval_required">Approval Required</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createGroupMutation.isPending}
                className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createGroupMutation.isPending ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
